import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { useAuth } from "@/store/authStore";
import { UserI } from "@/types/base";
import { trackAppOpen } from "@/api/analytics";
import { usePaymentStatusStore } from "@/store/paymentStatus";
import { useWelcomeInfo } from "@/store/welcomeInfo";
import ErrorFallback from "@/components/ErrorBoundary/ErrorFallback";
import { useSubscriptionStore } from "@/store/useSubscriptionStore";
import { getUser, getWasSubscribePayed, getAwardedBonuses } from "@/api/user";
import { useDcSyncUser } from "@/utils/hooks/useDcSyncUser";
import { useVersion } from "@/utils/hooks/useVersionCheck";

interface UserContextValue {
  user: UserI | null;
  userLoading: boolean;
  paymentOption: string | null;
  getWasSubscribePayedData: () => void;
  setPaymentOption: (elem: string) => void;
  refreshUser: (ignoreLoader?: boolean) => Promise<void>;
}

const UserContext = createContext<UserContextValue | undefined>(undefined);

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const { userId } = useAuth();
  const { isUpdating } = usePaymentStatusStore();
  const version = useVersion();

  const { setWasSubscribePayed, setAwardedBonus } = useWelcomeInfo();
  const {
    setIsSubscriptionActive,
    setIsSubscriptionCancelled,
  } = useSubscriptionStore();

  const [user, setUser] = useState<UserI | null>(null);
  const [userLoading, setUserLoading] = useState(true);

  const [paymentOption, setPaymentOption] = useState<string | null>(null);

  const loadData = useCallback(
    async (ignoreLoader?: boolean) => {
      if (!ignoreLoader) setUserLoading(true);
      try {
        const res = await getUser();
        setUser(res);

        if (
          res?.selected_activate_option === "orders" ||
          res?.selected_activate_option === "free_toggle"
        ) {
          setPaymentOption(res.selected_activate_option);
        } else {
          setPaymentOption(res.selected_payment_method_id);
        }

        const tillDate = new Date(res?.subscription_ends_at);
        if (tillDate) {
          tillDate.setUTCHours(20, 59, 59, 999);
        }

        const today = new Date();
        const isActive = res.cancelled_subscription
          ? tillDate
          : tillDate?.setDate(tillDate?.getDate() + 3);
        setIsSubscriptionActive(tillDate ? today <= isActive : false);

        setIsSubscriptionCancelled(res.cancelled_subscription);
      } catch (e) {
        console.error("Failed to load user:", e);
      } finally {
        setUserLoading(false);
      }
    },
    [userId]
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  useDcSyncUser({
    user,
    onSync: (updatedFields) =>
      setUser((currentUser) =>
        currentUser ? { ...currentUser, ...updatedFields } : currentUser
      ),
  });

  const getWasSubscribePayedData = async () => {
    if (!user) return;

    try {
      const res = await getWasSubscribePayed(user.telegram_id);
      setWasSubscribePayed(res);
    } catch (e) {
      console.log(e);
    }
  };

  const getAwardedBonusesData = async () => {
    if (!user) return;

    try {
      const bonusesData = await getAwardedBonuses(user.telegram_id);
      setAwardedBonus(bonusesData.data.text);
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    if (!user) return;
    getAwardedBonusesData();
    getWasSubscribePayedData();
  }, [user]);

  useEffect(() => {
    if (userId && user && version) {
      trackAppOpen({
        first_time: !user.funnel_status || user.funnel_status === "START",
        version,
      });
    }
  }, [userId, user, version]);

  useEffect(() => {
    if (!isUpdating) return;
    const id = setInterval(() => {
      loadData(true);
    }, 5000);

    return () => {
      clearInterval(id);
    };
  }, [isUpdating]);

  if (!userLoading && !user)
    return <ErrorFallback errorText="FAILED_TO_FETCH_USER" />;

  return (
    <UserContext.Provider
      value={{
        user,
        userLoading,
        paymentOption,
        getWasSubscribePayedData,
        setPaymentOption,
        refreshUser: loadData,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUserDataContext = (): UserContextValue => {
  const ctx = useContext(UserContext);
  if (!ctx) {
    throw new Error("useUserDataContext must be used within UserProvider");
  }
  return ctx;
};
